import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { profileImageSpec } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { useMarkProfileAssetMutation } from '../../../../../redux/api/fymAPI';
import type { HeaderPreviewGround } from '../../studio/spotifyHeader/FrameGround';
import { ProfileImageFrame } from '../frame/ProfileImageFrame';
import { profileDownloadUrl, profileFileGround } from '../profileAssets';
import { profileSlotChip } from '../profileChips';
import { profileSlotOf } from '../profileSlots';
import { profileMessages as m } from '../profileMessages';
import type { ProfileBannerKind } from '../profileRoute';
import { ProfileCard } from './ProfileCard';
import { ProfileChipBadge } from './ProfileChipBadge';
import { ProfileSlotNote } from './ProfileSlotNote';
import { ProfileDownloadLink } from './ProfileDownloadLink';
import { BANNER_CARDS, cardSourceLine } from './profileCardFacts';
import type { ProfileCardProps } from './profileCardProps';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The card of one banner (design a1, a2): the preview with its guides and the round photo of the
 * site, Edit (Make it when not made), Download and "I set it" (the manual mark, no API). The chip
 * reads "Set on YouTube" after the mark.
 */
const ProfileBannerCardComponent: React.FC<{ card: ProfileCardProps; kind: ProfileBannerKind }> = ({ card, kind }) => {
  const { accountId, profile, colours, avatarUrl, narrow, openTool, retry, isMaking } = card;
  const intl = useIntl();
  const [mark, markState] = useMarkProfileAssetMutation();
  const spec = profileImageSpec(kind);
  const slot = useMemo(() => profileSlotOf(profile.assets, kind), [kind, profile.assets]);
  const facts = BANNER_CARDS[kind];
  const ground: HeaderPreviewGround = profileFileGround(slot.current) ?? { kind: 'theme' };
  const download = profileDownloadUrl(slot.current);
  const { current } = slot;
  const toggleMark = () => {
    if (current === null) {
      return;
    }
    mark({ accountId, assetUuid: current.uuid, set: current.setAt === null }).catch(console.error);
  };

  return (
    <ProfileCard
      service={facts.service}
      name={intl.formatMessage(facts.name)}
      size={intl.formatMessage(facts.size)}
      chip={<ProfileChipBadge chip={profileSlotChip(slot)} testId={`profile-chip-${kind}`} />}
      testId={`profile-card-${kind}`}
    >
      <div className={styles.stage}>
        <ProfileImageFrame
          spec={spec}
          width={narrow ? Math.min(330, window.innerWidth - 56) : 420}
          ground={ground}
          colours={colours}
          guides={kind === 'youtubeBanner'}
          sitePhotoUrl={kind === 'youtubeBanner' ? undefined : avatarUrl}
          loading={slot.state === 'making' && current === null}
          testId={`profile-frame-${kind}`}
        />
      </div>
      <ProfileSlotNote
        slots={[slot]}
        name={intl.formatMessage(facts.name)}
        onTryAgain={() => retry([slot])}
        isBusy={isMaking}
      />
      <div className={styles.row}>
        <SMLButton className={styles.sm} onClick={() => openTool('banners', kind)}>
          {intl.formatMessage(current === null ? m.makeIt : m.edit)}
        </SMLButton>
        {download !== null && <ProfileDownloadLink href={download} testId={`profile-download-${kind}`} />}
        {current !== null && (
          <SMLButton
            className={styles.sm}
            disabled={markState.isLoading}
            onClick={toggleMark}
            ariaLabel={intl.formatMessage(current.setAt === null ? m.iSetIt : m.notSet)}
          >
            {intl.formatMessage(current.setAt === null ? m.iSetIt : m.notSet)}
          </SMLButton>
        )}
        <span className={styles.grow} />
        <span className={styles.meta}>
          {current === null ? intl.formatMessage(m.free) : cardSourceLine(intl, current)}
        </span>
      </div>
    </ProfileCard>
  );
};

export const ProfileBannerCard = TypedMemo(ProfileBannerCardComponent);
